import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Card, CardBody, CardTitle, Row, Col, Table, Badge } from "reactstrap";
import { FaUsers, FaBuilding, FaBriefcase, FaSitemap, FaCalendarAlt, FaMoneyBillWave, FaUserTie, FaClipboardList, FaBoxes } from "react-icons/fa";
import avatar1 from "../../assets/images/users/usericon.png";
import profileImg from "../../assets/images/profile-img.png";
import { ClipLoader } from "react-spinners";

// Import all GMS APIs
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getDepartments } from "../../components/Api/GmsDepartment";
import { getDesignations } from "../../components/Api/GmsDesignation";
import { getUsers } from "../../components/Api/GmsUser";
import { getEmployeeLeaves } from "../../components/Api/GmsEmployeeLeave";
import { getSalaryAdvances } from "../../components/Api/GmsSalaryAdvance";
import { getLeaveTypes } from "../../components/Api/GmsLeaveType";
import { getActivities } from "../../components/Api/GmsActivity";
import { getPayrollDetails } from "../../components/Api/GmsPayrollDetail";
import { getCompanies } from "../../components/Api/GmsCompany";
import { getProducts } from "../../components/Api/GmsProduct";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    // Organization Data
    totalEmployees: 0,
    totalCompanies: 0,
    totalBusinesses: 0,
    totalBranches: 0,
    totalDepartments: 0,
    totalDesignations: 0,
    totalUsers: 0,
    activeUsers: 0,
    
    // Employee Data
    departmentDistribution: [],
    monthlyEmployeeGrowth: [],
    
    // Leave Data
    totalLeaves: 0,
    pendingLeaves: 0,
    approvedLeaves: 0,
    leaveTypes: [],
    
    // Payroll Data
    totalAdvances: 0,
    pendingAdvances: 0,
    approvedAdvances: 0,
    totalAdvanceAmount: 0,
    
    // Activity Data
    totalActivities: 0,
    totalProducts: 0,
    totalPayrollRecords: 0
  });

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/Dashboard');
  const isViewOnly = hasViewOnlyPermission(permissions, '/Dashboard');
  const isAdmin = isUserAdmin();

  useEffect(() => {
    if (hasPermission) {
      loadDashboardData();
    } else {
      // Check if user has Spa Manager & Finance Dashboard permission
      const spaManagerFinancePermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaManagerFinanceDashboard');
      if (spaManagerFinancePermission) {
        navigate('/GmsSpaManagerFinanceDashboard');
        return;
      }
      // Check if user has Spa Employee Dashboard permission
      const spaDashboardPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaEmployeeDashboard');
      if (spaDashboardPermission) {
        navigate('/GmsSpaEmployeeDashboard');
      } else {
        navigate('/Dashboard'); // This will show access denied
      }
    }
  }, [hasPermission, navigate]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel for better performance
      const [
        employeesRaw,
        companiesRaw,
        businessesRaw,
        branchesRaw,
        departmentsRaw,
        designationsRaw,
        usersRaw,
        leavesRaw,
        advancesRaw,
        leaveTypesRaw,
        activitiesRaw,
        productsRaw,
        payrollDetailsRaw
      ] = await Promise.all([
        getEmployees({}).catch(() => []),
        getCompanies({}).catch(() => []),
        getBusinesses({}).catch(() => []),
        getBranches({}).catch(() => []),
        getDepartments({}).catch(() => []),
        getDesignations({}).catch(() => []),
        getUsers({}).catch(() => []),
        getEmployeeLeaves({}).catch(() => ({ data: [] })),
        getSalaryAdvances({}).catch(() => []),
        getLeaveTypes({}).catch(() => []),
        getActivities({}).catch(() => []),
        getProducts({}).catch(() => []),
        getPayrollDetails({}).catch(() => [])
      ]);

      // Normalize data to arrays (some APIs return single objects, others return arrays)
      const employees = Array.isArray(employeesRaw) ? employeesRaw : (employeesRaw ? [employeesRaw] : []);
      const companies = Array.isArray(companiesRaw) ? companiesRaw : (companiesRaw ? [companiesRaw] : []);
      const businesses = Array.isArray(businessesRaw) ? businessesRaw : (businessesRaw ? [businessesRaw] : []);
      const branches = Array.isArray(branchesRaw) ? branchesRaw : (branchesRaw ? [branchesRaw] : []);
      const departments = Array.isArray(departmentsRaw) ? departmentsRaw : (departmentsRaw ? [departmentsRaw] : []);
      const designations = Array.isArray(designationsRaw) ? designationsRaw : (designationsRaw ? [designationsRaw] : []);
      const users = Array.isArray(usersRaw) ? usersRaw : (usersRaw ? [usersRaw] : []);
      const activities = Array.isArray(activitiesRaw) ? activitiesRaw : (activitiesRaw ? [activitiesRaw] : []);
      const products = Array.isArray(productsRaw) ? productsRaw : (productsRaw ? [productsRaw] : []);
      const leaveTypes = Array.isArray(leaveTypesRaw) ? leaveTypesRaw : (leaveTypesRaw ? [leaveTypesRaw] : []);
      const advances = Array.isArray(advancesRaw) ? advancesRaw : [];
      const payrollDetails = Array.isArray(payrollDetailsRaw) ? payrollDetailsRaw : [];

      // Process leave data
      const leavesArray = Array.isArray(leavesRaw) ? leavesRaw : (leavesRaw?.data || []);

      // Calculate department distribution
      const deptCounts = {};
      employees.forEach(emp => {
        const dept = departments.find(d => d.department_Id === emp.employee_Department_ID);
        const deptName = dept?.department_NameEn || 'Unknown';
        deptCounts[deptName] = (deptCounts[deptName] || 0) + 1;
      });

      const departmentDistribution = Object.entries(deptCounts).map(([name, value]) => ({ name, value }));

      // Calculate monthly employee growth (last 6 months)
      const monthlyGrowth = calculateMonthlyGrowth(employees);

      // Process leave data - check for pending vs approved (based on rejoin date)
      const pendingLeaves = leavesArray.filter(leave => !leave.leave_RejoinDate && !leave.is_Cancelled);
      const approvedLeaves = leavesArray.filter(leave => leave.leave_RejoinDate && !leave.is_Cancelled);

      // Process salary advance data
      const pendingAdvances = advances.filter(adv => !adv.approved_Date);
      const approvedAdvances = advances.filter(adv => adv.approved_Date);
      const totalAdvanceAmount = approvedAdvances.reduce((sum, adv) => sum + (adv.approved_Amount || 0), 0);

      // Process user data
      const activeUsers = users.filter(user => user.user_Active !== false);

      setDashboardData({
        // Organization Metrics
        totalEmployees: employees.length,
        totalCompanies: companies.length,
        totalBusinesses: businesses.length,
        totalBranches: branches.length,
        totalDepartments: departments.length,
        totalDesignations: designations.length,
        totalUsers: users.length,
        activeUsers: activeUsers.length,
        
        // Employee Data
        departmentDistribution,
        monthlyEmployeeGrowth: monthlyGrowth,
        
        // Leave Data
        totalLeaves: leavesArray.length,
        pendingLeaves: pendingLeaves.length,
        approvedLeaves: approvedLeaves.length,
        leaveTypes: leaveTypes.length,
        
        // Payroll Data
        totalAdvances: advances.length,
        pendingAdvances: pendingAdvances.length,
        approvedAdvances: approvedAdvances.length,
        totalAdvanceAmount,
        
        // Activity Data
        totalActivities: activities.length,
        totalProducts: products.length,
        totalPayrollRecords: payrollDetails.length
      });

    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMonthlyGrowth = (employees) => {
    const months = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      
      const employeesUpToMonth = employees.filter(emp => {
        const joinDate = new Date(emp.employee_JoinDate || emp.employee_CreatedDate);
        return joinDate <= date;
      }).length;
      
      months.push({ month: monthName, count: employeesUpToMonth });
    }
    
    return months;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container-fluid px-4 d-flex justify-content-center align-items-center" style={{ marginTop: "120px", minHeight: "500px" }}>
        <div className="text-center">
          <ClipLoader size={50} color="#556ee6" />
          <p className="mt-3 text-muted">Loading Dashboard Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4" style={{ marginTop: "120px", paddingBottom: "20px" }}>
      {/* Welcome Section */}
      <Card className="overflow-hidden mb-4 shadow">
        <div className="bg-primary-subtle">
          <Row>
            <Col xs="7">
              <div className="text-primary p-3">
                <h5 className="text-primary">Welcome Back!</h5>
                <p className="mb-0">HRMS Dashboard - Real-time Analytics</p>
              </div>
            </Col>
            <Col xs="5" className="align-self-end">
              <img src={profileImg} alt="" className="img-fluid" style={{ height: "100px" }} />
            </Col>
          </Row>
        </div>
        <CardBody className="pt-0">
          <Row>
            <Col sm="4">
              <div className="avatar-md profile-user-wid mb-4">
                <img
                  src={avatar1}
                  alt="User Avatar"
                  className="img-thumbnail rounded-circle"
                />
              </div>
              <h5 className="font-size-15 text-truncate">Admin</h5>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Organization Summary Cards */}
      <Row className="mb-4">
        <Col xl={3} md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h4 className="mb-2">{dashboardData.totalEmployees}</h4>
                  <p className="text-muted mb-0">Total Employees</p>
                </div>
                <div className="avatar-sm rounded-circle bg-primary bg-soft p-2">
                  <FaUsers className="font-size-24" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h4 className="mb-2">{dashboardData.totalCompanies}</h4>
                  <p className="text-muted mb-0">Companies</p>
                </div>
                <div className="avatar-sm rounded-circle bg-secondary bg-soft p-2">
                  <FaBuilding className="font-size-24" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h4 className="mb-2">{dashboardData.totalBusinesses}</h4>
                  <p className="text-muted mb-0">Businesses</p>
                </div>
                <div className="avatar-sm rounded-circle bg-info bg-soft p-2">
                  <FaBriefcase className="font-size-24" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h4 className="mb-2">{dashboardData.totalDepartments}</h4>
                  <p className="text-muted mb-0">Departments</p>
                </div>
                <div className="avatar-sm rounded-circle bg-success bg-soft p-2">
                  <FaSitemap className="font-size-24" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col xl={3} md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h4 className="mb-2">{dashboardData.totalDesignations}</h4>
                  <p className="text-muted mb-0">Designations</p>
                </div>
                <div className="avatar-sm rounded-circle bg-warning bg-soft p-2">
                  <FaBriefcase className="font-size-24" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h4 className="mb-2">{dashboardData.totalBranches}</h4>
                  <p className="text-muted mb-0">Branches</p>
                </div>
                <div className="avatar-sm rounded-circle bg-info bg-soft p-2">
                  <FaBuilding className="font-size-24" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h4 className="mb-2">{dashboardData.activeUsers}/{dashboardData.totalUsers}</h4>
                  <p className="text-muted mb-0">Active Users</p>
                </div>
                <div className="avatar-sm rounded-circle bg-secondary bg-soft p-2">
                  <FaUserTie className="font-size-24" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h4 className="mb-2">{dashboardData.totalActivities}</h4>
                  <p className="text-muted mb-0">Activities</p>
                </div>
                <div className="avatar-sm rounded-circle bg-purple bg-soft p-2">
                  <FaClipboardList className="font-size-24" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

            {/* HR Operations Summary */}
      <Row className="mb-4">
        <Col xl={3} md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h4 className="mb-2">{dashboardData.pendingLeaves}</h4>
                  <p className="text-muted mb-0">Pending Leaves</p>
                </div>
                <div className="avatar-sm rounded-circle bg-warning bg-soft p-2">
                  <FaCalendarAlt className="font-size-24" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h4 className="mb-2">{dashboardData.pendingAdvances}</h4>
                  <p className="text-muted mb-0">Pending Advances</p>
                </div>
                <div className="avatar-sm rounded-circle bg-danger bg-soft p-2">
                  <FaMoneyBillWave className="font-size-24" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h4 className="mb-2">{dashboardData.totalProducts}</h4>
                  <p className="text-muted mb-0">Products</p>
                </div>
                <div className="avatar-sm rounded-circle bg-success bg-soft p-2">
                  <FaBoxes className="font-size-24" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        </Row>

      <Row>
        {/* Employee Growth Chart */}
        <Col lg={8} className="mb-4">
          <Card className="shadow">
            <CardBody>
              <CardTitle tag="h5" className="text-primary mb-4">Employee Growth (Last 6 Months)</CardTitle>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dashboardData.monthlyEmployeeGrowth}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#556ee6" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>

        {/* Department Distribution */}
        <Col lg={4} className="mb-4">
          <Card className="shadow">
            <CardBody>
              <CardTitle tag="h5" className="text-primary mb-4">Department Distribution</CardTitle>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dashboardData.departmentDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {dashboardData.departmentDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>
      </Row>




    </div>
  );
};

export default Dashboard;
